import { useEffect, useRef, useState } from "react";
import { MdExpandMore, MdOutlineWhatsapp } from "react-icons/md";
import { useApplication } from "../../../contexts/ApplicationContext";
import { Badge } from "../Primitives";
import { whatsappLink } from "../crmHelpers";
import {
  CONTACT_TEMPLATES,
  describeContact,
  renderTemplate,
  templateForQueue,
} from "../contactTemplates";

/*
  Contato em um clique.

  Abre o WhatsApp com a mensagem pronta para a situação da conta e registra o contato
  na hora. Antes, falar com a conta e registrar eram dois passos, e o segundo era o que
  ficava para depois: a fila continuava sugerindo quem já tinha recebido mensagem.

  Cada opção é um link de verdade, e não um botão que chama `window.open`: assim o
  navegador não trata a nova aba como pop-up, e dá para abrir com o botão do meio.
*/
const ContactMenu = ({
  account,
  phoneNumber,
  guidance,
  onRegister,
  buttonClassName = "",
  menuAlign = "right",
}) => {
  const { user } = useApplication();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        // Dentro do painel lateral, o Esc fecha só o menu.
        event.stopPropagation();
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  if (!whatsappLink(phoneNumber)) return null;

  const context = {
    account: { ...guidance?.account, ...account },
    paymentUrl: guidance?.paymentUrl,
    agentName: user?.name,
  };

  // A sugestão segue a fila da conta que não está em espera, na ordem de urgência.
  const suggested = templateForQueue(guidance?.primary?.id);
  const templates = suggested
    ? [suggested, ...CONTACT_TEMPLATES.filter((item) => item.id !== suggested.id)]
    : CONTACT_TEMPLATES;

  const register = (template, message) => {
    setOpen(false);
    onRegister?.({ type: "WHATSAPP", body: describeContact(template, message) });
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((previous) => !previous)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={`inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700 ${buttonClassName}`}
      >
        <MdOutlineWhatsapp size={16} /> WhatsApp
        <MdExpandMore size={16} />
      </button>

      {open ? (
        <div
          role="menu"
          className={`absolute z-40 mt-1.5 max-h-[60vh] w-72 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl ${
            menuAlign === "right" ? "right-0" : "left-0"
          }`}
        >
          <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Abrir com a mensagem
          </p>

          {templates.map((template) => {
            const message = renderTemplate(template, context);

            return (
              <a
                key={template.id}
                role="menuitem"
                href={whatsappLink(phoneNumber, message)}
                target="_blank"
                rel="noreferrer"
                onClick={() => register(template, message)}
                className="flex items-center justify-between gap-2 px-3 py-2 text-sm text-slate-700 transition hover:bg-emerald-50"
              >
                <span className="min-w-0 truncate">{template.label}</span>
                {suggested?.id === template.id ? (
                  <Badge tone="success">sugerida</Badge>
                ) : null}
              </a>
            );
          })}

          <a
            role="menuitem"
            href={whatsappLink(phoneNumber)}
            target="_blank"
            rel="noreferrer"
            onClick={() => register(null)}
            className="block border-t border-slate-100 px-3 py-2 text-sm text-slate-500 transition hover:bg-slate-50"
          >
            Abrir sem mensagem
          </a>
        </div>
      ) : null}
    </div>
  );
};

export default ContactMenu;
